import { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useUserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { hasUserRetailerAccess, isFreeTier } from "../utils/userUtils";
import { getAllRetailers } from "../config/retailers";
import { useProductUrlSync } from "../context/ProductContext";
import AuthPage from "../pages/AuthPage";
import ForgotPasswordPage from "../pages/ForgotPasswordPage";
import ResetPasswordPage from "../pages/ResetPasswordPage";
import PasswordResetFlow from "./PasswordResetFlow";
// Logged-out "/" serves the online deals feed (pages/HomeFeedPage.tsx). The
// previous marketing landing (pages/LandingPage.tsx) is kept but unrouted; the
// reseller landing lives at /resellers.
import HomeFeedPage from "../pages/HomeFeedPage";
import HomeDealPage from "../pages/HomeDealPage";
import ResellerLandingPage from "../pages/ResellerLandingPage";
import LandingOptionsPage from "../pages/LandingOptionsPage";
import AppRoot from "../pages/AppRoot";
import ProductsPage from "../pages/ProductsPage";
import DollarGeneralCouponCombosPage from "../pages/DollarGeneralCouponCombosPage";
// CVS client pilot paused; keep CVSCouponCombosPage source disconnected for later.
import ProductSearchPage from "../pages/ProductSearchPage";
import TracksPage from "../pages/TracksPageNew";
import VerificationPage from "../pages/VerificationPage";
import OnboardingPage from "../pages/OnboardingPage";
import SetUpPage from "../pages/SetUpPage";
import PaywallPage from "../pages/PaywallPage";
import YoureInPage from "../pages/YoureInPage";
import MoreStoresPage from "../pages/MoreStoresPage";
import SettingsPage from "../pages/SettingsPage";
import BotFiltersPage from "../pages/BotFiltersPage";
import HelpPage from "../pages/HelpPage";
import AdminPage from "../pages/AdminPage";
import NotFoundPage from "../pages/NotFoundPage";
import ExtensionPage from "../pages/ExtensionPage";
import ProductDetailPage from "../pages/ProductDetailPage";
import DiscordCallbackPage from "../pages/DiscordCallbackPage";
import GoogleCallbackPage from "../pages/GoogleCallbackPage";
import SupportPage from "../pages/SupportPage";
import SuccessPage from "../pages/SuccessPage";
import LeadsPage from "../pages/LeadsPage";
import ContactUsPage from "../pages/ContactUsPage";
import PrivacyPolicyPage from "../pages/PrivacyPolicyPage";
import TermsOfServicePage from "../pages/TermsOfServicePage";
import PennyItemsProPage from "../pages/PennyItemsProPage";
import PennyItemsBetaPage from "../pages/PennyItemsBetaPage";
import DeleteAccountPage from "../pages/DeleteAccountPage";
import AiChatPage from "../pages/AiChatPage";
import FeaturedDealsPage from "../pages/FeaturedDealsPage";
import HawkFindsPage from "../pages/HawkFindsPage";
import CommunitySuccessPage from "../pages/CommunitySuccessPage";
import PublicDealsRoute, { LegacyDealsRedirect } from "./PublicDealsRoute";
import InstallRedirectPage from "../pages/InstallRedirectPage";
import BlogPage from "../pages/BlogPage";
import BlogPostPage from "../pages/BlogPostPage";
import FeaturePage from "../pages/FeaturePage";
import GuidePage from "../pages/GuidePage";
import GuidesIndexPage from "../pages/GuidesIndexPage";
import ToolPage from "../pages/ToolPage";
import ToolsIndexPage from "../pages/ToolsIndexPage";
import StoreProductionDesignPreviewPage from "../pages/StoreProductionDesignPreviewPage";
import { LogfrogRouteSync } from "./LogfrogRouteSync";
import { MetaPixelRouteSync } from "./MetaPixelRouteSync";
import useAffiliateMobileInstallRedirect from "../hooks/useAffiliateMobileInstallRedirect";
import { initReferralCapture } from "../services/AttributionService";
import { buildGetStartedUrl, sanitizeInternalReturnTo } from "../utils/authReturn";
import {
  isAuthenticationRequiredPath,
  isFreePublicFeedPath,
  publicPathForMemberPath,
} from "./AppRouter.utils";
import { CLEARANCE_PATH, getPublicDealsRetailer } from "../config/publicDeals";

// Members-only page. A signed-in free account has the public deals site instead (as in
// pinto), so it is sent to the matching public store page ("/walmart/products" →
// "/walmart"), or "/" when the store has no public feed. Paid users and admins pass.
function MembersOnly({ children }: { children: React.ReactNode }) {
  const { user } = useUserContext();
  const location = useLocation();
  if (user && isFreeTier(user)) {
    return <Navigate to={publicPathForMemberPath(location.pathname)} replace />;
  }
  return <>{children}</>;
}

// The public feed routes ("/walmart", "/walmart/toys", "/walmart/item/<key>") for a
// signed-in free account. Paid users keep today's behavior there (NotFound).
function FreePublicFeedRoute({ page }: { page: "feed" | "deal" }) {
  const { user } = useUserContext();
  const location = useLocation();
  // Members have the full clearance feed; the public Clearance page is for free accounts.
  if (user && !isFreeTier(user) && location.pathname === CLEARANCE_PATH) return <Navigate to="/" replace />;
  if (!user || !isFreeTier(user)) return <NotFoundPage />;
  if (!isFreePublicFeedPath(location.pathname)) {
    return <Navigate to={publicPathForMemberPath(location.pathname)} replace />;
  }
  return page === "deal" ? <HomeDealPage /> : <HomeFeedPage />;
}

// "/deals" and "/deals/<store>" are the old public catalog; free accounts get the
// store's public page. Shared-deal ids keep the signed-in redirect for everyone.
function SignedInDealsRoute({ retailerSlug }: { retailerSlug?: string }) {
  const { user } = useUserContext();
  const { dealId } = useParams<{ dealId: string }>();
  const retailer = getPublicDealsRetailer(retailerSlug || dealId);
  if (user && isFreeTier(user) && retailer) {
    return <Navigate to={`/${retailer.slug}`} replace />;
  }
  return <PublicDealsRoute authenticated retailerSlug={retailerSlug} />;
}

const AUTH_ENTRY_PATHS = ["/get-started", "/login", "/register", "/signup"];

// A signed-in visit to an auth entry path (bookmarked /login, "Log in" with a
// live session, an OAuth error bounce) goes to its returnTo or home instead of
// falling through to the 404 route.
function SignedInAuthRedirect() {
  const [searchParams] = useSearchParams();
  const returnTo = sanitizeInternalReturnTo(searchParams.get("returnTo"));
  const target =
    returnTo && !AUTH_ENTRY_PATHS.includes(new URL(returnTo, "https://dealhawk.invalid").pathname)
      ? returnTo
      : "/";
  return <Navigate to={target} replace />;
}

// Component to handle retailer routing - redirects to first retailer if not in retailer path
function RetailerRedirectWrapper({ children }: { children: React.ReactNode }) {
  const { user } = useUserContext();
  const location = useLocation();
  const hasRetailers = !!(user?.retailers && user.retailers.length > 0);

  // If user is set up and verified, check if they're in a retailer path
  if (user && hasRetailers && user.verified) {
    const path = location.pathname;
    const retailers = getAllRetailers().filter((retailer) => hasUserRetailerAccess(user, retailer));

    // Check if current path is a global path (not retailer-specific)
    const globalPaths = [
      "/setup",
      "/onboarding",
      "/settings",
      "/delete-account",
      "/help",
      "/support",
      "/admin",
      "/verify",
      "/success",
      "/extension",
      "/online",
      "/privacy",
      "/terms",
      "/hawk-ai",
      "/hot-deals",
      "/hawk-finds",
      "/community-successes",
    ];
    const isGlobalPath = globalPaths.some((globalPath) =>
      path.startsWith(globalPath)
    );

    // Check if current path is already a retailer path
    const isRetailerPath = retailers.some((retailer) =>
      path.startsWith(`/${retailer}/`)
    );

    // If not in a global path and not in a retailer path, redirect to first retailer
    if (!isGlobalPath && !isRetailerPath && path === "/") {
      const firstRetailer = retailers[0];
      if (firstRetailer) {
        return <Navigate to={`/${firstRetailer}/products`} replace />;
      }
    }
  }

  return <>{children}</>;
}

// Component that handles URL synchronization inside Router context
export function RouterContent() {
  const { user, isLoading } = useUserContext();
  const { t } = useTranslation();
  const location = useLocation();
  const hasRetailers = !!(user?.retailers && user.retailers.length > 0);

  useAffiliateMobileInstallRedirect();

  useEffect(() => initReferralCapture(), []);

  // Sync selected product with URL
  useProductUrlSync();

  if (import.meta.env.DEV && location.pathname === "/__design/store-production") {
    return <StoreProductionDesignPreviewPage />;
  }

  // Show loading screen while determining authentication status
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-dark-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-dark-300">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  if (location.pathname === "/landing-options") {
    return <LandingOptionsPage />;
  }

  if (!user && isAuthenticationRequiredPath(location.pathname)) {
    return (
      <Navigate
        to={buildGetStartedUrl(
          `${location.pathname}${location.search}${location.hash}`,
          "protected_route"
        )}
        replace
      />
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<AppRoot authed={false} />}>
          <Route path="/" element={<HomeFeedPage />} />
          <Route path="/resellers" element={<ResellerLandingPage />} />
          <Route path="/get-started" element={<AuthPage />} />
          {/* Legacy auth paths redirect to the unified flow */}
          <Route path="/login" element={<Navigate to="/get-started" replace />} />
          <Route path="/register" element={<Navigate to="/get-started" replace />} />
          <Route path="/signup" element={<Navigate to="/get-started" replace />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/contact-us" element={<ContactUsPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/install" element={<InstallRedirectPage />} />
          <Route path="/deals" element={<LegacyDealsRedirect />} />
          <Route path="/deals/:dealId" element={<PublicDealsRoute />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/features/:slug" element={<FeaturePage />} />
          <Route path="/guides" element={<GuidesIndexPage />} />
          <Route path="/guides/:slug" element={<GuidePage />} />
          <Route path="/tools" element={<ToolsIndexPage />} />
          <Route path="/tools/:slug" element={<ToolPage />} />
          <Route
            path="/auth/discord/callback"
            element={<DiscordCallbackPage />}
          />
          <Route
            path="/auth/google/callback"
            element={<GoogleCallbackPage />}
          />
          {/* Store pages of the feed: /walmart, /walmart/deals, /walmart/toys.
              Static routes above win; unknown stores render NotFound. */}
          <Route path="/:retailerSlug" element={<HomeFeedPage />} />
          <Route path="/:retailerSlug/item/:productKey" element={<HomeDealPage />} />
          <Route path="/:retailerSlug/:section" element={<HomeFeedPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<AppRoot authed={true} />}>
        {/* Global routes that don't require retailer context */}
        <Route path="/verify" element={<VerificationPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        {/* After checkout: "You're in" → "Where else do you shop?" → deals.
            Under /onboarding/ so AppRoot renders them full-screen. */}
        <Route path="/onboarding/scanning" element={<YoureInPage />} />
        <Route path="/onboarding/more-stores" element={<MoreStoresPage />} />
        <Route path="/success" element={<SuccessPage />} />
        <Route
          path="/setup"
          element={
            user && hasRetailers ? (
              <Navigate to="/" replace />
            ) : (
              <PaywallPage />
            )
          }
        />
        {/* Legacy setup page - kept for reference or switching back */}
        <Route
          path="/setup-legacy"
          element={
            user && hasRetailers ? (
              <Navigate to="/" replace />
            ) : (
              <SetUpPage />
            )
          }
        />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/deals" element={<SignedInDealsRoute retailerSlug="walmart" />} />
        <Route path="/deals/:dealId" element={<SignedInDealsRoute />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />
        {/* Signed-in password change (Settings → Profile), plus the reset-email
            links, which used to 404 for anyone already signed in. */}
        <Route
          path="/change-password"
          element={<PasswordResetFlow signedIn initialEmail={user.userId?.includes("@") ? user.userId : ""} />}
        />
        <Route
          path="/forgot-password"
          element={<PasswordResetFlow signedIn initialEmail={user.userId?.includes("@") ? user.userId : ""} />}
        />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route
          path="/hot-deals"
          element={
            <MembersOnly>
              <FeaturedDealsPage />
            </MembersOnly>
          }
        />
        <Route path="/featured-deals" element={<Navigate to="/hot-deals" replace />} />
        {/* Flips disabled for now */}
        {/* <Route
          path="/flips"
          element={
            <MembersOnly>
              <ArbitragePage />
            </MembersOnly>
          }
        />
        <Route path="/arbitrage" element={<Navigate to="/flips" replace />} /> */}
        <Route
          path="/hawk-finds"
          element={
            <MembersOnly>
              <HawkFindsPage />
            </MembersOnly>
          }
        />
        {/* Community Success is viewable by every signed-in member, including free. Posting and commenting require a paid plan. */}
        <Route path="/community-successes" element={<CommunitySuccessPage />} />
        <Route
          path="/hawk-ai"
          element={
            <MembersOnly>
              <AiChatPage />
            </MembersOnly>
          }
        />
        <Route path="/contact-us" element={<ContactUsPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/install" element={<InstallRedirectPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/features/:slug" element={<FeaturePage />} />
        <Route path="/guides" element={<GuidesIndexPage />} />
        <Route path="/guides/:slug" element={<GuidePage />} />
        <Route path="/tools" element={<ToolsIndexPage />} />
        <Route path="/tools/:slug" element={<ToolPage />} />
        <Route path="/admin/users" element={<AdminPage />} />
        <Route
          path="/home_depot/penny-items-pro"
          element={
            <MembersOnly>
              <PennyItemsProPage />
            </MembersOnly>
          }
        />
        <Route
          path="/home_depot/penny-items-beta"
          element={
            <MembersOnly>
              <PennyItemsBetaPage />
            </MembersOnly>
          }
        />
        <Route
          path="/extension"
          element={
            <MembersOnly>
              <ExtensionPage />
            </MembersOnly>
          }
        />
        <Route
          path="/online/woot"
          element={<Navigate to="/woot/products" replace />}
        />
        <Route
          path="/walmart_online/*"
          element={<Navigate to="/walmart/products?storeId=online" replace />}
        />
        <Route
          path="/amazon_online/*"
          element={<Navigate to="/amazon/products?storeId=online" replace />}
        />
        <Route
          path="/kohls_online/*"
          element={<Navigate to="/kohls/products?storeId=online" replace />}
        />

        {/* Root path: the public home feed for free accounts; paid users go to their first retailer */}
        <Route
          path="/"
          element={
            isFreeTier(user) ? (
              <HomeFeedPage />
            ) : (
              <RetailerRedirectWrapper>
                <ProductsPage />
              </RetailerRedirectWrapper>
            )
          }
        />

        {/* Retailer-specific routes */}
        <Route
          path="/:retailer/products"
          element={
            <MembersOnly>
              <ProductsPage />
            </MembersOnly>
          }
        />
        <Route
          path="/dollar_general/coupons"
          element={
            <MembersOnly>
              <DollarGeneralCouponCombosPage />
            </MembersOnly>
          }
        />
        {/* CVS client pilot paused. Block products, combos, and deep links. */}
        <Route path="/cvs/*" element={<Navigate to="/" replace />} />
        <Route
          path="/:retailer/product-search"
          element={
            <MembersOnly>
              <ProductSearchPage />
            </MembersOnly>
          }
        />
        <Route
          path="/:retailer/product/:productId"
          element={
            <MembersOnly>
              <ProductDetailPage />
            </MembersOnly>
          }
        />
        <Route
          path="/:retailer/product"
          element={
            <MembersOnly>
              <ProductDetailPage />
            </MembersOnly>
          }
        />
        <Route
          path="/:retailer/tracking"
          element={
            <MembersOnly>
              <TracksPage />
            </MembersOnly>
          }
        />
        <Route
          path="/:retailer/bot-filters"
          element={
            <MembersOnly>
              <BotFiltersPage />
            </MembersOnly>
          }
        />
        <Route
          path="/:retailer/leads"
          element={
            <MembersOnly>
              <LeadsPage />
            </MembersOnly>
          }
        />

        {/* Legacy routes - redirect to walmart for backward compatibility */}
        <Route
          path="/product-search"
          element={<Navigate to="/walmart/product-search" replace />}
        />
        <Route
          path="/product"
          element={<Navigate to="/walmart/product" replace />}
        />
        <Route
          path="/favorites"
          element={<Navigate to="/walmart/favorites" replace />}
        />
        <Route
          path="/tracking"
          element={<Navigate to="/walmart/tracking" replace />}
        />
        <Route
          path="/bot-filters"
          element={<Navigate to="/walmart/bot-filters" replace />}
        />

        {AUTH_ENTRY_PATHS.map((path) => (
          <Route key={path} path={path} element={<SignedInAuthRedirect />} />
        ))}

        {/* OAuth callbacks - must be available in both auth states */}
        <Route path="/auth/discord/callback" element={<DiscordCallbackPage />} />
        <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />

        {/* The public deals site for signed-in free accounts (NotFound for paid users,
            as before). Static routes above, like /:retailer/products, win. */}
        <Route path="/:retailerSlug" element={<FreePublicFeedRoute page="feed" />} />
        <Route path="/:retailerSlug/item/:productKey" element={<FreePublicFeedRoute page="deal" />} />
        <Route path="/:retailerSlug/:section" element={<FreePublicFeedRoute page="feed" />} />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <LogfrogRouteSync />
      <MetaPixelRouteSync />
      <RouterContent />
    </BrowserRouter>
  );
}
